import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, MenuProps, message, Spin, Tooltip } from 'antd';
import { DownOutlined, MailOutlined } from '@ant-design/icons';

import {
  CourseInstanceAttendee,
  DisplayedCourseInstance,
} from '../types/CourseInstance';
import { t } from 'i18next';
import CertificateAPI from '../../certificate/CertificateAPI';
import { CertificateLanguage } from '../../certificate/types/Certificate';
import { getCertificateEmailTooltip } from '../utils/certificateUtils';
import { twMerge } from 'tailwind-merge';

type SendCertificateEmailDropdownProps = {
  courseInstance: DisplayedCourseInstance;
  selectedAttendees: CourseInstanceAttendee[];
  certificateLanguages: CertificateLanguage[];
  loadingCertificateLanguages: boolean;
  onSuccess?: () => void;
};

const SendCertificateEmailDropdown: React.FC<
  SendCertificateEmailDropdownProps
> = ({
  courseInstance,
  selectedAttendees,
  certificateLanguages,
  loadingCertificateLanguages,
  onSuccess,
}) => {
  const [
    certificateNotDownloadableTooltip,
    setCertificateNotDownloadableTooltip,
  ] = useState<string>();
  const [messageApi, contextHolder] = message.useMessage();
  const [submitting, setSubmitting] = useState(false);

  const loading = useMemo(() => {
    return loadingCertificateLanguages || submitting;
  }, [loadingCertificateLanguages, submitting]);

  const conditionalClassNames = twMerge(
    !certificateNotDownloadableTooltip && 'border-blue-300',
    loading && 'bg-grayLight',
  );

  const sendCertificateEmail = useCallback<NonNullable<MenuProps['onClick']>>(
    ({ key }) => {
      if (
        certificateNotDownloadableTooltip ||
        !courseInstance.startsAt ||
        !courseInstance.id ||
        key == '-1'
      ) {
        return;
      }

      const attendeeIds = selectedAttendees
        .map((a) => a.id)
        .filter((id) => id !== null && id !== undefined);
      const attendeeElsaIds = selectedAttendees
        .map((a) => a.elsaId)
        .filter((id) => id !== null && id !== undefined);

      if (
        (attendeeIds.length === 0 && attendeeElsaIds.length === 0) ||
        (attendeeIds.length > 0 && attendeeElsaIds.length > 0)
      ) {
        throw Error("Attendees must either have an 'id' or an 'elsaId'");
      }

      const numberOfAttendees =
        attendeeIds.length > 0 ? attendeeIds.length : attendeeElsaIds.length;

      setSubmitting(true);
      CertificateAPI.sendCertificateEmails(
        courseInstance.id,
        parseInt(key),
        attendeeIds,
        attendeeElsaIds,
      )
        .then(() => {
          messageApi.open({
            type: 'success',
            content: t('components.SendCertificateEmailDropdown.emailSuccess', {
              count: numberOfAttendees,
            }),
          });
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch(() => {
          messageApi.open({
            type: 'error',
            content: t('components.SendCertificateEmailDropdown.emailFailure'),
          });
        })
        .finally(() => setSubmitting(false));
    },
    [
      certificateNotDownloadableTooltip,
      courseInstance.id,
      courseInstance.startsAt,
      messageApi,
      onSuccess,
      selectedAttendees,
    ],
  );

  useEffect(() => {
    setCertificateNotDownloadableTooltip(
      getCertificateEmailTooltip(
        selectedAttendees,
        courseInstance,
        certificateLanguages,
      ),
    );
  }, [certificateLanguages, courseInstance, selectedAttendees]);

  return (
    <>
      {contextHolder}
      <Dropdown
        trigger={['click']}
        className={!!certificateNotDownloadableTooltip ? 'bg-gray-100' : ''}
        menu={{
          items: certificateLanguages.map((language) => {
            return {
              label: language.name,
              key: language.id.toString(),
            };
          }),
          onClick: sendCertificateEmail,
        }}>
        <Tooltip title={certificateNotDownloadableTooltip}>
          <Button
            disabled={
              !!certificateNotDownloadableTooltip ||
              !courseInstance.id ||
              loading
            }
            className={`flex border-1 shadow-none rounded h-10 items-center font-medium ${conditionalClassNames}`}>
            <MailOutlined
              style={{ fontSize: '18px', color: '#305E7A' }}
              className="pr-1"
            />
            {t('components.SendCertificateEmailDropdown.emailCertificates')}
            <div className="flex items-center pl-2">
              {loading ? <Spin size="small" /> : <DownOutlined />}
            </div>
          </Button>
        </Tooltip>
      </Dropdown>
    </>
  );
};

export default SendCertificateEmailDropdown;
